import { useState, useMemo, useEffect } from "react";
import { usePageSEO } from "@/hooks/usePageSEO";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, Sparkles, Check, Search, Users, Plus, X, Languages, Compass, BookOpen } from "lucide-react";
import { SCHOOL_SYSTEMS, SUBJECTS, AVATARS, getSpecialtiesForLevel, type Specialty } from "@/data/schoolSystems";
import OnboardingGuide from "@/components/guide/OnboardingGuide";
import DobPicker from "@/components/DobPicker";
import BirthPlaceSelect from "@/components/BirthPlaceSelect";
import { deriveBirthCountryCode } from "@/lib/birthCountryCode";

const STUDY_LANGUAGES = [
  { value: "fr", label: "Français", emoji: "🇫🇷" },
  { value: "en", label: "Anglais", emoji: "🇬🇧" },
  { value: "ar", label: "Arabe", emoji: "🇸🇦" },
  { value: "es", label: "Espagnol", emoji: "🇪🇸" },
  { value: "pt", label: "Portugais", emoji: "🇵🇹" },
  { value: "de", label: "Allemand", emoji: "🇩🇪" },
  { value: "it", label: "Italien", emoji: "🇮🇹" },
  { value: "other", label: "Autre", emoji: "🌐" },
];

const Onboarding = () => {
  usePageSEO({
    title: "Configuration de ton compte | KelasAI",
    description: "Configure ton profil KelasAI en quelques étapes : niveau scolaire, matières, objectifs et avatar.",
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [countryCode, setCountryCode] = useState("");
  const [level, setLevel] = useState("");
  const [schoolClass, setSchoolClass] = useState("");
  const [subjects, setSubjects] = useState<string[]>([]);
  const [goals, setGoals] = useState("");
  const [avatar, setAvatar] = useState("👦");
  const [saving, setSaving] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [userRole, setUserRole] = useState<string | null>(null);
  const [customSubject, setCustomSubject] = useState("");
  const [studyLanguage, setStudyLanguage] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [customSpecialty, setCustomSpecialty] = useState("");

  // Personal info state
  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [gender, setGender] = useState("");

  // Parent-specific state
  const [childMatricule, setChildMatricule] = useState("");
  const [linkingChild, setLinkingChild] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("role, full_name, date_of_birth, birth_place, gender, avatar_url, country, level, school_class, subjects, goals, study_language, specialty, onboarding_step").eq("id", user.id).single().then(({ data }) => {
      if (data) {
        setUserRole(data.role);
        if (data.full_name) setFullName(data.full_name);
        if (data.date_of_birth) setDateOfBirth(data.date_of_birth);
        if (data.birth_place) setBirthPlace(data.birth_place);
        if ((data as any).gender) setGender((data as any).gender);
        if (data.avatar_url && data.avatar_url.startsWith("http")) setAvatar(data.avatar_url);
        if ((data as any).country) setCountryCode((data as any).country);
        if ((data as any).level) setLevel((data as any).level);
        if ((data as any).school_class) setSchoolClass((data as any).school_class);
        if (Array.isArray((data as any).subjects) && (data as any).subjects.length > 0) setSubjects((data as any).subjects);
        if ((data as any).goals) setGoals((data as any).goals);
        if ((data as any).study_language) setStudyLanguage((data as any).study_language);
        if ((data as any).specialty) setSpecialty((data as any).specialty);

        // Resume from email link or last saved step
        const resume = searchParams.get("resume") === "true";
        const stepParam = searchParams.get("step") || (resume ? (data as any).onboarding_step : null);
        if (stepParam) {
          const map: Record<string, number> = {
            personal_info: 0, country: 1, level: 2, class: 3, specialty: 4,
            language: 5, subjects: 6, goals: 7, avatar: 8,
          };
          const idx = map[stepParam];
          if (typeof idx === "number") setStep(idx);
        }
      }
    });
  }, [user, searchParams]);

  // Persist current step on each change so we know where the user stopped.
  useEffect(() => {
    if (!user) return;
    const STEP_NAMES = ["personal_info", "country", "level", "class", "specialty", "language", "subjects", "goals", "avatar"];
    const name = STEP_NAMES[step] || "personal_info";
    supabase.from("profiles").update({ onboarding_step: name } as any).eq("id", user.id).then(() => {});
  }, [step, user]);

  const isParent = userRole === "parent";
  const isTeacher = userRole === "enseignant";

  const selectedSystem = SCHOOL_SYSTEMS.find((s) => s.code === countryCode);
  const selectedLevel = selectedSystem?.levels.find((l) => l.value === level);

  // Spécialités proposées en fonction du niveau choisi
  const specialties: Specialty[] = useMemo(() => getSpecialtiesForLevel(level), [level]);
  const hasSpecialties = specialties.length > 0;
  const selectedSpecialty = specialties.find((s) => s.value === specialty);
  const isOtherSpecialty = !!specialty && /^autre/i.test(specialty);

  // Quand l'utilisateur sélectionne une spécialité, pré-cocher les matières recommandées
  const pickSpecialty = (s: Specialty) => {
    setSpecialty(s.value);
    setSubjects((prev) => {
      const merged = new Set(prev);
      s.recommendedSubjects.forEach((r) => merged.add(r));
      return Array.from(merged);
    });
  };

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return SCHOOL_SYSTEMS;
    const q = countrySearch.toLowerCase();
    return SCHOOL_SYSTEMS.filter((s) => s.country.toLowerCase().includes(q) || s.code.toLowerCase().includes(q));
  }, [countrySearch]);

  const toggleSubject = (s: string) =>
    setSubjects((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const addCustomSubject = () => {
    const v = customSubject.trim();
    if (!v) return;
    if (v.length > 40) {
      toast.error("Matière trop longue (40 caractères max)");
      return;
    }
    if (subjects.some((s) => s.toLowerCase() === v.toLowerCase())) {
      toast.info("Cette matière est déjà sélectionnée");
      setCustomSubject("");
      return;
    }
    setSubjects((prev) => [...prev, v]);
    setCustomSubject("");
  };

  const isPredefinedSubject = (val: string) => SUBJECTS.some((s) => s.value === val);

  // Parent flow: 3 steps (personal info + matricule + avatar)
  // Student flow: 9 steps (personal info, country, level, class, specialty, language, subjects, goals, avatar)
  // L'étape "specialty" est skippée automatiquement si le niveau ne propose pas d'options.
  const parentSteps = 3;
  const studentSteps = 9;
  const teacherSteps = 2;
  const totalSteps = isTeacher ? teacherSteps : isParent ? parentSteps : studentSteps;

  const canNextStudent =
    step === 0 ? !!fullName.trim() && !!gender :
    step === 1 ? !!countryCode :
    step === 2 ? !!level :
    step === 3 ? !!schoolClass :
    step === 4 ? (!hasSpecialties || (!!specialty && (!isOtherSpecialty || !!customSpecialty.trim()))) :
    step === 5 ? !!studyLanguage :
    step === 6 ? subjects.length > 0 :
    step === 7 ? !!goals.trim() :
    true;

  const canNextTeacher =
    step === 0 ? !!fullName.trim() && !!gender :
    true;

  const canNextParent =
    step === 0 ? !!fullName.trim() && !!gender :
    // Step 1 (matricule) is optional — always allow next
    true;

  const canNext = isTeacher ? canNextTeacher : isParent ? canNextParent : canNextStudent;

  const linkChildAndSave = async () => {
    if (!user) return;
    setSaving(true);

    const code = childMatricule.trim().toUpperCase();

    // Matricule is optional — only attempt linking if provided
    if (code.length >= 5) {
      const { error: linkError } = await supabase
        .rpc("link_current_tuteur_to_apprenant_by_matricule", { input_matricule: code });

      if (linkError) {
        const msg = linkError.message?.toLowerCase() || "";
        if (msg.includes("matricule introuvable")) {
          toast.error("Matricule invalide. Vérifiez le numéro de matricule de votre enfant.");
        } else if (msg.includes("already") || msg.includes("duplicate") || msg.includes("unique")) {
          toast.error("Cet enfant est déjà lié à votre compte.");
        } else {
          toast.error(linkError.message || "Erreur lors de la liaison.");
        }
        setSaving(false);
        return;
      }
    }

    // Update parent profile
    const { error } = await supabase.from("profiles").update({
      full_name: fullName.trim(),
      date_of_birth: dateOfBirth || null,
      birth_place: birthPlace.trim() || null,
      birth_country: deriveBirthCountryCode(birthPlace) || null,
      gender,
      avatar_url: avatar,
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
      onboarding_step: "completed",
    } as any).eq("id", user.id);

    setSaving(false);
    if (error) {
      toast.error("Erreur lors de la sauvegarde");
      return;
    }

    toast.success(code.length >= 5
      ? "Enfant ajouté avec succès ! Bienvenue sur KelasAI 🎉"
      : "Bienvenue sur KelasAI 🎉 Vous pourrez ajouter un enfant à tout moment.");
    navigate("/parent-dashboard");
  };

  const saveStudent = async () => {
    if (!user) return;
    setSaving(true);
    // Auto-set role to "etudiant" for university-level users
    const effectiveRole = level === "universite" ? "etudiant" : (userRole === "etudiant" && level !== "universite" ? "eleve" : userRole);
    const { error } = await supabase.from("profiles").update({
      full_name: fullName.trim(),
      date_of_birth: dateOfBirth || null,
      birth_place: birthPlace.trim() || null,
      birth_country: deriveBirthCountryCode(birthPlace) || null,
      gender,
      country: countryCode,
      level,
      school_class: schoolClass,
      subjects,
      goals: goals.trim(),
      avatar_url: avatar,
      study_language: studyLanguage,
      specialty: isOtherSpecialty && customSpecialty.trim() ? customSpecialty.trim() : specialty,
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
      onboarding_step: "completed",
      ...(effectiveRole && effectiveRole !== userRole ? { role: effectiveRole } : {}),
    } as any).eq("id", user.id);
    setSaving(false);

    if (error) {
      toast.error("Erreur lors de la sauvegarde");
      return;
    }
    toast.success("Bienvenue sur KelasAI ! 🎉");
    navigate("/dashboard");
  };

  const saveTeacher = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from("profiles").update({
      full_name: fullName.trim(),
      date_of_birth: dateOfBirth || null,
      birth_place: birthPlace.trim() || null,
      birth_country: deriveBirthCountryCode(birthPlace) || null,
      gender,
      avatar_url: avatar,
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
      onboarding_step: "completed",
    } as any).eq("id", user.id);
    setSaving(false);
    if (error) {
      toast.error("Erreur lors de la sauvegarde");
      return;
    }
    toast.success("Bienvenue sur KelasAI ! 🎉");
    navigate("/teacher-dashboard");
  };

  const handleFinish = () => {
    if (isTeacher) {
      saveTeacher();
    } else if (isParent) {
      linkChildAndSave();
    } else {
      saveStudent();
    }
  };

  // ─── Personal Info Step (shared) ───
  const personalInfoStep = (
    <div key="personal-info" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Tes informations 📝</h2>
      <p className="text-sm text-muted-foreground font-medium">Quelques infos pour personnaliser ton expérience</p>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-bold text-foreground mb-1.5 block">Nom complet *</label>
          <div className="flex items-center gap-3 rounded-2xl px-4 py-3 bg-muted">
            <span className="text-lg">👤</span>
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
              placeholder="Prénom Nom"
              className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none" />
          </div>
        </div>
        <div>
          <label className="text-sm font-bold text-foreground mb-1.5 block">Je suis *</label>
          <div className="grid grid-cols-2 gap-3">
            {(isParent || userRole === "enseignant" ? [
              { value: "M", label: "Homme", emoji: "👨" },
              { value: "F", label: "Femme", emoji: "👩" },
            ] : [
              { value: "M", label: "Garçon", emoji: "👦" },
              { value: "F", label: "Fille", emoji: "👧" },
            ]).map((g) => (
              <button key={g.value} type="button" onClick={() => setGender(g.value)}
                className={`p-3 rounded-2xl border-2 flex items-center justify-center gap-2 transition-all ${gender === g.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                <span className="text-lg">{g.emoji}</span>
                <span className="font-bold text-sm text-foreground">{g.label}</span>
                {gender === g.value && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-bold text-foreground mb-1.5 block">Je suis né(e) le</label>
          <DobPicker value={dateOfBirth} onChange={setDateOfBirth} />
        </div>
        <div>
          <label className="text-sm font-bold text-foreground mb-1.5 block">Je suis né(e) à</label>
          <BirthPlaceSelect value={birthPlace} onChange={setBirthPlace} />
        </div>
      </div>
    </div>
  );

  // ─── Parent Steps ───
  const parentStepViews = [
    personalInfoStep,
    // Step 1: Enter child matricule
    <div key="matricule" className="space-y-4">
      <h2 className="text-xl font-black text-foreground flex items-center gap-2">
        <Users className="w-5 h-5 text-primary" /> Connecter votre enfant 👨‍👩‍👧
      </h2>
      <p className="text-sm text-muted-foreground font-medium">
        Entrez le <strong className="text-foreground">numéro de matricule</strong> de votre enfant pour accéder à son suivi
        (format : KEL-XXXXXX). Cette étape est <strong className="text-foreground">optionnelle</strong> — vous pourrez le
        faire plus tard depuis votre espace parent.
      </p>
      <div className="space-y-2">
        <label className="text-sm font-bold text-foreground">Matricule de l'enfant (optionnel)</label>
        <div className="flex items-center gap-3 bg-muted rounded-2xl px-4 py-3">
          <span className="text-lg">🎓</span>
          <input type="text" value={childMatricule} onChange={(e) => setChildMatricule(e.target.value.toUpperCase())}
            placeholder="KEL-A1B2C3" className="flex-1 bg-transparent text-sm font-bold text-foreground placeholder:text-muted-foreground outline-none tracking-wider" maxLength={10} />
        </div>
        <p className="text-xs text-muted-foreground">💡 Vous pouvez compléter ces informations plus tard.</p>
      </div>
      <button
        type="button"
        onClick={() => { setChildMatricule(""); setStep(step + 1); }}
        className="w-full py-2.5 rounded-2xl border-2 border-dashed border-border text-sm font-bold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        Ignorer cette étape →
      </button>
      <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-4 mt-2">
        <p className="text-xs font-semibold text-foreground mb-1">🔒 Sécurité</p>
        <p className="text-xs text-muted-foreground">Seul un matricule valide permet d'accéder aux données de l'élève.</p>
      </div>
    </div>,
    // Step 2: Avatar
    <div key="avatar" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Choisis ton avatar ! 🎨</h2>
      <p className="text-sm text-muted-foreground font-medium">Il apparaîtra dans votre espace parent</p>
      <div className="grid grid-cols-4 gap-3">
        {AVATARS.map((a) => (
          <button key={a} onClick={() => setAvatar(a)}
            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl border-2 transition-all mx-auto ${avatar === a ? "border-primary bg-primary/5 scale-110" : "border-border hover:border-primary/30"}`}>
            {a}
          </button>
        ))}
      </div>
    </div>,
  ];

  // ─── Student Steps ───
  const studentStepViews = [
    // Step 0: Personal info
    personalInfoStep,
    // Step 1: Country
    <div key="country" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Dans quel pays étudies-tu ? 🌍</h2>
      <p className="text-sm text-muted-foreground font-medium">On adaptera le système scolaire</p>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={countrySearch}
          onChange={(e) => setCountrySearch(e.target.value)}
          placeholder="Rechercher un pays..."
          className="w-full pl-10 pr-4 py-3 rounded-2xl bg-muted text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none border-2 border-border focus:border-primary transition-colors"
        />
      </div>
      <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
        {filteredCountries.map((s) => (
          <button key={s.code} onClick={() => { setCountryCode(s.code); setLevel(""); setSchoolClass(""); }}
            className={`p-4 rounded-2xl border-2 text-left transition-all ${countryCode === s.code ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
            <span className="text-2xl">{s.flag}</span>
            <p className="font-bold text-sm text-foreground mt-1">{s.country}</p>
          </button>
        ))}
      </div>
    </div>,

    // Step 1: Level
    <div key="level" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Quel est ton niveau ? 📖</h2>
      <p className="text-sm text-muted-foreground font-medium">Système scolaire : {selectedSystem?.country} {selectedSystem?.flag}</p>
      <div className="grid grid-cols-1 gap-3">
        {selectedSystem?.levels.map((l) => (
          <button key={l.value} onClick={() => { setLevel(l.value); setSchoolClass(""); }}
            className={`p-4 rounded-2xl border-2 text-left transition-all flex items-center gap-3 ${level === l.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
            <span className="text-2xl">{l.emoji}</span>
            <div>
              <p className="font-bold text-sm text-foreground">{l.label}</p>
              <p className="text-xs text-muted-foreground">{l.classes.length} classes</p>
            </div>
            {level === l.value && <Check className="w-5 h-5 text-primary ml-auto" />}
          </button>
        ))}
      </div>
    </div>,

    // Step 2: Class
    <div key="class" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Quelle classe ? 🏫</h2>
      <p className="text-sm text-muted-foreground font-medium">{selectedLevel?.label} {selectedLevel?.emoji}</p>
      <div className="grid grid-cols-2 gap-3">
        {selectedLevel?.classes.map((c) => (
          <button key={c.value} onClick={() => setSchoolClass(c.value)}
            className={`p-3 rounded-2xl border-2 text-center transition-all ${schoolClass === c.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
            <p className="font-bold text-sm text-foreground">{c.label}</p>
            {schoolClass === c.value && <Check className="w-4 h-4 text-primary mx-auto mt-1" />}
          </button>
        ))}
      </div>
    </div>,

    // Step 4: Spécialité / option (sautée si pas de spécialités pour ce niveau)
    <div key="specialty" className="space-y-4">
      <h2 className="text-xl font-black text-foreground flex items-center gap-2">
        <Compass className="w-5 h-5 text-primary" /> Ton option / spécialité 🎯
      </h2>
      <p className="text-sm text-muted-foreground font-medium">
        {selectedLevel?.label} {selectedLevel?.emoji} — choisis ta filière. KelasAI te proposera des cours adaptés.
      </p>
      <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto pr-1">
        {specialties.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => pickSpecialty(s)}
            className={`p-4 rounded-2xl border-2 text-left transition-all ${specialty === s.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0">{s.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-foreground">{s.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
              </div>
              {specialty === s.value && <Check className="w-5 h-5 text-primary shrink-0" />}
            </div>
          </button>
        ))}
      </div>

      {/* Aperçu des cours/matières recommandés */}
      {selectedSpecialty && selectedSpecialty.recommendedSubjects.length > 0 && (
        <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-4 h-4 text-primary" />
            <p className="text-xs font-bold text-foreground uppercase tracking-wide">Cours recommandés pour toi</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedSpecialty.recommendedSubjects.map((code) => {
              const sub = SUBJECTS.find((x) => x.value === code);
              if (!sub) return null;
              return (
                <span key={code} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background border border-primary/30 text-xs font-bold text-foreground">
                  <span className="text-sm">{sub.emoji}</span> {sub.label}
                </span>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Ces matières sont pré-cochées à l'étape suivante. Tu pourras les modifier.</p>
        </div>
      )}

      {isOtherSpecialty && (
        <div className="space-y-2 pt-2">
          <label className="text-sm font-bold text-foreground">Précise ta spécialité ✍️</label>
          <div className="flex items-center gap-2 bg-muted rounded-2xl px-3 py-2.5">
            <span className="text-base">🎯</span>
            <input
              type="text"
              value={customSpecialty}
              onChange={(e) => setCustomSpecialty(e.target.value)}
              placeholder="Ex: Architecture, Médecine vétérinaire..."
              maxLength={60}
              className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none"
            />
          </div>
          <p className="text-xs text-muted-foreground">Indique le nom de ta filière pour adapter tes cours.</p>
        </div>
      )}
    </div>,

    // Step 5: Study language
    <div key="language" className="space-y-4">
      <h2 className="text-xl font-black text-foreground flex items-center gap-2">
        <Languages className="w-5 h-5 text-primary" /> Langue d'études 🗣️
      </h2>
      <p className="text-sm text-muted-foreground font-medium">
        Dans quelle langue suis-tu tes cours ? KelasAI t'expliquera dans cette langue.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {STUDY_LANGUAGES.map((l) => (
          <button key={l.value} onClick={() => setStudyLanguage(l.value)}
            className={`p-3 rounded-2xl border-2 flex items-center gap-2 transition-all ${studyLanguage === l.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
            <span className="text-lg">{l.emoji}</span>
            <span className="font-bold text-sm text-foreground">{l.label}</span>
            {studyLanguage === l.value && <Check className="w-4 h-4 text-primary ml-auto" />}
          </button>
        ))}
      </div>
    </div>,

    // Step 5: Subjects (avec matière personnalisée)
    <div key="subjects" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Tes matières préférées ? 🎯</h2>
      <p className="text-sm text-muted-foreground font-medium">Choisis-en au moins une, ou ajoute la tienne</p>
      <div className="grid grid-cols-2 gap-3">
        {SUBJECTS.map((s) => (
          <button key={s.value} onClick={() => toggleSubject(s.value)}
            className={`p-3 rounded-2xl border-2 flex items-center gap-2 transition-all ${subjects.includes(s.value) ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
            <span className="text-lg">{s.emoji}</span>
            <span className="font-bold text-sm text-foreground">{s.label}</span>
            {subjects.includes(s.value) && <Check className="w-4 h-4 text-primary ml-auto" />}
          </button>
        ))}
      </div>

      {/* Champ matière personnalisée */}
      <div className="pt-2 border-t border-border space-y-2">
        <label className="text-sm font-bold text-foreground">Ajouter une matière perso ✨</label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 flex-1 bg-muted rounded-2xl px-3 py-2.5">
            <span className="text-base">📚</span>
            <input
              type="text"
              value={customSubject}
              onChange={(e) => setCustomSubject(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomSubject(); } }}
              placeholder="Ex: Robotique, Latin..."
              maxLength={40}
              className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none"
            />
          </div>
          <button
            type="button"
            onClick={addCustomSubject}
            disabled={!customSubject.trim()}
            className="p-2.5 rounded-2xl bg-primary text-primary-foreground disabled:opacity-40 hover:opacity-90 transition-opacity"
            aria-label="Ajouter"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Liste des matières personnalisées sélectionnées */}
        {subjects.filter((s) => !isPredefinedSubject(s)).length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {subjects.filter((s) => !isPredefinedSubject(s)).map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-xs font-bold text-foreground">
                {s}
                <button type="button" onClick={() => toggleSubject(s)} aria-label={`Retirer ${s}`} className="hover:text-destructive">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>,

    // Step 6: Goals
    <div key="goals" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Ton objectif ? 🚀</h2>
      <p className="text-sm text-muted-foreground font-medium">En une phrase, qu'est-ce que tu veux accomplir ?</p>
      <textarea value={goals} onChange={(e) => setGoals(e.target.value)}
        placeholder="Ex: Réussir mon brevet, comprendre les maths..."
        className="w-full p-4 rounded-2xl bg-muted text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none resize-none h-28 border-2 border-border focus:border-primary transition-colors"
        maxLength={200} />
      <p className="text-xs text-muted-foreground text-right">{goals.length}/200</p>
    </div>,

    // Step 7: Avatar
    <div key="avatar" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Choisis ton avatar ! 🎨</h2>
      <p className="text-sm text-muted-foreground font-medium">Il apparaîtra dans le chat</p>
      <div className="grid grid-cols-4 gap-3">
        {AVATARS.map((a) => (
          <button key={a} onClick={() => setAvatar(a)}
            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl border-2 transition-all mx-auto ${avatar === a ? "border-primary bg-primary/5 scale-110" : "border-border hover:border-primary/30"}`}>
            {a}
          </button>
        ))}
      </div>
    </div>,
  ];

  const teacherStepViews = [
    personalInfoStep,
    <div key="avatar-teacher" className="space-y-4">
      <h2 className="text-xl font-black text-foreground">Choisissez votre avatar ! 🎨</h2>
      <p className="text-sm text-muted-foreground font-medium">Il apparaîtra dans votre espace enseignant</p>
      <div className="grid grid-cols-4 gap-3">
        {AVATARS.map((a) => (
          <button key={a} onClick={() => setAvatar(a)}
            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl border-2 transition-all mx-auto ${avatar === a ? "border-primary bg-primary/5 scale-110" : "border-border hover:border-primary/30"}`}>
            {a}
          </button>
        ))}
      </div>
    </div>,
  ];

  const currentSteps = isTeacher ? teacherStepViews : isParent ? parentStepViews : studentStepViews;

  // Wait for role to load
  if (userRole === null) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "var(--gradient-hero)" }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-black text-gradient">KelasAI</span>
          </div>
          {isParent && (
            <p className="text-sm font-bold text-primary mb-2">👨‍👩‍👧 Mode Parent</p>
          )}
          {/* Progress */}
          <div className="flex gap-2 justify-center mt-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all ${i <= step ? "w-8 bg-primary" : "w-4 bg-muted"}`} />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2 font-medium">Étape {step + 1}/{totalSteps}</p>
        </div>

        <div className="card-edu p-6" style={{ boxShadow: "var(--shadow-md)" }}>
          <OnboardingGuide step={step} isParent={isParent} />
          {currentSteps[step]}

          <div className="flex gap-3 mt-6">
            {step > 0 && (
              <button onClick={() => {
                // Si on revient sur l'étape spécialité (4) mais qu'elle n'existe pas, sauter à 3
                let target = step - 1;
                if (!isParent && target === 4 && !hasSpecialties) target = 3;
                setStep(Math.max(0, target));
              }} className="px-4 py-3 rounded-2xl border-2 border-border text-sm font-bold text-foreground hover:bg-muted transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={step < totalSteps - 1 ? () => {
                // Sauter l'étape spécialité (index 4) si le niveau ne propose pas d'options
                let target = step + 1;
                if (!isParent && target === 4 && !hasSpecialties) target = 5;
                setStep(target);
              } : handleFinish}
              disabled={!canNext || saving}
              className="btn-hero flex-1 py-3 justify-center text-sm disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : step < totalSteps - 1 ? <><span>Suivant</span><ArrowRight className="w-4 h-4" /></> : <><span>{isParent ? "Accéder au suivi" : "Commencer !"}</span><Sparkles className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
